/** ARRAY — AutoCAD's Array dialog, in the two forms this office's drawings actually contain.
 *
 * ⚠⚠ **NON-ASSOCIATIVE, AND IT SAYS SO ON THE DIALOG.** AutoCAD 2012 and later make an
 * ASSOCIATIVE array by default: one object, an anonymous block, whose row and column counts stay
 * editable afterwards. That needs the parametric machinery `docs/GAP.md` puts in layer 12. This
 * makes real, separate copies — exactly what `ARRAYCLASSIC` does with associativity off — and a
 * drafter who believes the count is still editable would find out at the worst moment.
 *
 * ⚠ A POLAR array asks for its centre on the DRAWING, not in this box: the centre is a place,
 * and a drafter picks it off the geometry rather than typing coordinates for it.
 */
import { useEffect, useState } from "react";

export interface RectSpec { rows: number; cols: number; drow: number; dcol: number }
export interface PolarSpec { count: number; angle: number }

export default function ArrayDialog({ kind, onRect, onPolar, onClose }: {
  kind: "rect" | "polar";
  onRect: (s: RectSpec) => void;
  onPolar: (s: PolarSpec) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState("3");
  const [cols, setCols] = useState("4");
  const [drow, setDrow] = useState("1000");
  const [dcol, setDcol] = useState("1000");
  const [count, setCount] = useState("6");
  const [angle, setAngle] = useState("360");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const go = () => {
    if (kind === "rect") {
      const s = { rows: Number(rows), cols: Number(cols),
                  drow: Number(drow), dcol: Number(dcol) };
      if (![s.rows, s.cols].every((n) => Number.isInteger(n) && n >= 1)) {
        setErr("rows and columns are whole numbers, 1 or more"); return;
      }
      if (!Number.isFinite(s.drow) || !Number.isFinite(s.dcol)) {
        setErr("a spacing is a number"); return;
      }
      // ⚠ Caught HERE as well as on the server, because the server's message arrives after a
      // round trip and this one is the answer to a box the drafter is still looking at.
      if (s.rows > 1 && s.drow === 0) { setErr("rows need a spacing"); return; }
      if (s.cols > 1 && s.dcol === 0) { setErr("columns need a spacing"); return; }
      onRect(s);
      return;
    }
    const n = Number(count), a = Number(angle);
    if (!Number.isInteger(n) || n < 2) { setErr("a polar array needs at least 2 items"); return; }
    if (!Number.isFinite(a) || a === 0) { setErr("an angle to fill is a number, not 0"); return; }
    onPolar({ count: n, angle: a });
  };

  const field = (label: string, v: string, set: (s: string) => void, id: string) => (
    <label style={{ display: "grid", gap: 3 }}>
      <span style={{ color: "#9aa0a6" }}>{label}</span>
      <input value={v} data-testid={id} onChange={(e) => { setErr(null); set(e.target.value); }}
             onKeyDown={(e) => { if (e.key === "Enter") go(); }} style={inp} />
    </label>);

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 420, background: "#3c3f41", border: "1px solid #555",
                    borderRadius: 4, boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          {kind === "rect" ? "Rectangular Array" : "Polar Array"}
        </div>
        <div style={{ padding: 12, font: "11.5px system-ui", display: "grid", gap: 10 }}>
          {kind === "rect" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {field("Rows", rows, setRows, "array-rows")}
              {field("Columns", cols, setCols, "array-cols")}
              {field("Row spacing", drow, setDrow, "array-drow")}
              {field("Column spacing", dcol, setDcol, "array-dcol")}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {field("Items (including the original)", count, setCount, "array-count")}
              {field("Angle to fill", angle, setAngle, "array-angle")}
            </div>)}
          {kind === "polar" && (
            <div style={{ color: "#9aa0a6", lineHeight: 1.55 }}>
              The centre is picked on the drawing after this box closes.
              {/* ⚠ 360 ÷ items, not ÷ (items − 1). Eight items round a full circle are 45°
                  apart; the other reading gives 51.4° and an array with a visible gap. */}
              <div>⚠ A full circle divides by the item count, so the last item does not
                   land on the first.</div>
            </div>)}
          <div style={{ color: "#c8a35a", lineHeight: 1.55 }}>
            ⚠ This makes separate copies. It is not an associative array — the count cannot be
            changed afterwards.
          </div>
          {err && <div style={{ color: "#ff8f8f" }}>{err}</div>}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: "0 12px 12px" }}>
          <button style={btn} onClick={onClose} data-testid="array-cancel">Cancel</button>
          <button style={{ ...btn, background: "#5a6570" }} onClick={go}
                  data-testid="array-ok">OK</button>
        </div>
      </div>
    </div>);
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "3px 6px", borderRadius: 2, font: "12px system-ui", width: "100%",
  boxSizing: "border-box",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
